import { z } from 'zod';

/**
 * Path-safe upload identifier: a short slug-shaped value (3-128 chars,
 * starts alphanumeric).
 */
export const UploadId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{2,127}$/, 'invalid upload id');
export type UploadId = z.infer<typeof UploadId>;

export const UploadParams = z.object({
  uploadId: UploadId,
});
export type UploadParams = z.infer<typeof UploadParams>;

/**
 * Per-file caps applied at the route boundary. PDFs are capped at 50 MiB;
 * images at 10 MiB to keep per-request token budgets sane.
 */
export const MAX_UPLOAD_BYTES_PDF = 50 * 1024 * 1024;
export const MAX_UPLOAD_BYTES_IMAGE = 10 * 1024 * 1024;

/**
 * Combined per-request cap (sum of pending composer attachments plus all
 * thread-sticky uploads bound to the active thread) and the per-request
 * image-count cap.
 */
export const MAX_REQUEST_BYTES = 50 * 1024 * 1024;
export const MAX_IMAGES_PER_REQUEST = 100;

/**
 * MIME allowlist for user uploads. The tuple ordering matters: the frontend
 * joins this list verbatim into the file picker's `accept` attribute.
 */
export const ALLOWED_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

export const AllowedUploadMime = z.enum(ALLOWED_MIMES);
export type AllowedUploadMime = z.infer<typeof AllowedUploadMime>;

/**
 * Client-facing Upload row. The Foundry file id is intentionally NOT in
 * this shape — it is server-only state stored on the DB row and used by the
 * chat handler to project file parts into the OpenAI request. Callers that
 * need the server-side shape inside `apps/api` declare their own internal
 * type rather than widen this contract.
 */
export const Upload = z
  .object({
    id: z.string(),
    userId: z.string(),
    threadId: z.string().nullable(),
    filename: z.string(),
    mimeType: AllowedUploadMime,
    sizeBytes: z.number().int().nonnegative(),
    status: z.enum(['uploading', 'parsing', 'ready', 'failed']),
    kind: z.enum(['pdf', 'image']),
    foundryExpiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type Upload = z.infer<typeof Upload>;

export const UploadList = z.object({
  items: z.array(Upload),
  total: z.number().int().nonnegative(),
});
export type UploadList = z.infer<typeof UploadList>;

export const UploadQuery = z.object({
  threadId: z.string().optional(),
});
export type UploadQuery = z.infer<typeof UploadQuery>;

/**
 * Wire shape of a per-message attachment reference. The client sends a
 * list of these inside `ChatRequestBody.attachments`; the server resolves
 * each one to a `foundryFileId` via `requireOwnedUpload` before projecting
 * it into the OpenAI request. The Foundry file id is intentionally NOT in
 * this shape — it never crosses the wire in either direction.
 */
export const AttachmentRef = z
  .object({
    uploadId: z.string().uuid(),
    mediaType: AllowedUploadMime,
    filename: z.string().min(1).max(255),
  })
  .strict();
export type AttachmentRef = z.infer<typeof AttachmentRef>;
